import React, { ChangeEvent, useState } from "react";

import { Avatar, Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningIcon from "@mui/icons-material/Warning";

import { SearchField, DrawerControl, StatusChip, Drawer } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";

import { RiskDetails } from "./RiskDetails";
import { ScroreCard } from "./ScroreCard";
import { RaiseRisk } from "./RaiseRisk";

import { useRiskRegister } from "../mutation";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { RiskHeader } from "./RiskDetailsOverview";
import { useSearchParams } from "react-router-dom";

interface RiskRegisterDataProps {
    "risk_id": string,
    "project_ref": string,
    "project_name": string,
    "project_id": number,
    "title": string,
    "risk_description": string,
    "review_date": string,
    "category_display_text": string,
    "cause_title": string,
    "status": string,
    "impact": string,
    "probability": string,
    "divident": number,
    "risk_ref": null,
    "score": number,
    "severity": string,
    "status_display_text": string,
    "color": string,
    "assessment_status": string,
    "impact_display": string,
    "probability_display": string
};

export const RiskRegister: React.FC<{ projectId: string | undefined }> = ({ projectId }) => {

    const { can } = useCapabilities();
    const isAllowedToRaiseRisk = can("operational_delivery:project:risk:add");

    const [searchParams, setSearchParams] = useSearchParams();

    const selected = searchParams.get('selected');
    const id = searchParams.get('id');

    const [searchDraft, setSearchDraft] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');

    const { isPending: isRiskRegisterPending, riskRegisterData, refetch } = useRiskRegister({ projectId, searchKey });

    const [raiseRisk, setRaiseRisk] = useState<{
        open: boolean,
        editStatus: boolean,
        riskId: string | null
    }>({
        open: false,
        editStatus: false,
        riskId: null
    });

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
    };

    const handleClear = () => {
        setSearchDraft('');
        setSearchKey('');
    };

    const handleSearch = () => {
        setSearchKey(searchDraft);
    };

    const openDrawer = (riskId: string) => {
        const params = new URLSearchParams(searchParams);
        params.set("selected", "true");
        params.set("id", riskId);
        setSearchParams(params, { replace: true });
    };

    const closeDrawer = () => {
        const params = new URLSearchParams(searchParams);
        params.delete("selected");
        params.delete("id");
        setSearchParams(params, { replace: true });
    };

    if (isRiskRegisterPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box p={2}>

            <Box display={'flex'} alignItems={'center'} gap={2} mb={2}>
                <SearchField
                    label="Search Risks"
                    searchTerm={searchDraft}
                    handleSearch={handleChangeSearch}
                    handleClearSearch={handleClear}
                    handleApplySearch={handleSearch}
                />
                {
                    isAllowedToRaiseRisk && (
                        <Button startIcon={<AddIcon />} sx={{ ml: "auto" }} onClick={() => setRaiseRisk({ open: true, editStatus: false, riskId: null })}>
                            RAISE RISK
                        </Button>
                    )
                }
            </Box>

            <Box display={'flex'} flexDirection={'column'} gap={2}>
                {
                    riskRegisterData.length === 0 ?
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            alignContent="center"
                            width="100%"
                            height="100%"
                            gap={2}
                            mt={5}
                        >
                            <Typography variant="body2" color="text.secondary">No risks found.</Typography>
                            <Typography variant="body2" color="text.secondary">Try adjusting your search or filter to find what you're looking for.</Typography>
                        </Box> :
                        riskRegisterData.map((value: RiskRegisterDataProps, key: number) => (
                            <Card sx={{ py: 1.5, px: 2 }} key={key} onClick={() => { openDrawer(value.risk_id) }}>
                                <Box display={'flex'} gap={3} alignItems={'center'}>
                                    <Avatar sx={{ bgcolor: value.status_display_text === 'Open' ? 'warning.main' : 'divider', borderRadius: 1 }}>
                                        <WarningIcon />
                                    </Avatar>
                                    <Box display={'flex'} flexDirection={'column'} gap={.8}>
                                        <Box display={'flex'} gap={2} alignItems={'center'}>
                                            <Typography variant="subtitle2">{value.title}</Typography>
                                            <StatusChip label={value.status_display_text} color={value.status_display_text === 'Open' ? 'amber' : 'default'} />
                                        </Box>
                                        <Box display={'flex'} gap={.5}>
                                            <Typography variant="caption" color="text.secondary">{value.project_name || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.project_ref || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.risk_ref || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{value.category_display_text || 'N/A'}</Typography>
                                            <Typography variant="caption" color="text.secondary">•</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatDate(value?.review_date?.toString())}</Typography>
                                        </Box>
                                    </Box>
                                    <Typography variant="subtitle2" ml={'auto'}>{value.assessment_status}</Typography>
                                    <Typography variant="caption" color="text.secondary">Impact</Typography>
                                    <StatusChip label={String(value.impact_display || 'N/A')} color={(value.impact === 'high' || value.impact === 'very_high') ? "red" : value.impact === 'medium' ? "amber" : (value.impact === 'low' || value.impact === 'very_low') ? "green" : "default"} variant="filled" />
                                    <Typography variant="caption" color="text.secondary">Probability</Typography>
                                    <StatusChip label={String(value.probability_display || 'N/A')} color={(value.probability === 'high' || value.probability === 'very_high') ? "red" : value.probability === 'medium' ? "amber" : (value.probability === 'low' || value.probability === 'very_low') ? "green" : "default"} variant="filled" />
                                    <Typography variant="caption" color="text.secondary">Score</Typography>
                                    <ScroreCard score={value.score} />
                                </Box>
                            </Card>
                        ))
                }
            </Box>

            <Drawer
                isOpen={!!selected}
                header={
                    !!id &&
                    <RiskHeader
                        riskId={String(id)}
                        handleClose={() => closeDrawer()}
                    />
                }
            >
                <RiskDetails
                    riskId={String(id)}
                    onEdit={() => {
                        setRaiseRisk({ open: true, editStatus: true, riskId: String(id) });
                        closeDrawer();
                    }}
                    refetch={refetch}
                />
            </Drawer>

            <DrawerControl
                width="35%"
                title="Add/Edit Risk"
                subTitle={""}
                isOpen={raiseRisk.open}
                handleClose={() => { setRaiseRisk({ open: false, editStatus: false, riskId: null }) }}
                children={
                    <RaiseRisk
                        riskId={raiseRisk.riskId}
                        editStatus={raiseRisk.editStatus}
                        projectId={projectId}
                        onCancel={() => setRaiseRisk({ open: false, editStatus: false, riskId: null })}
                        refetch={refetch}
                    />
                }
            />

        </Box>
    );
};